// Teams page (under The CRO Zone). Two columns (70/30): LEFT = the rep grid (rep · Team · Region ·
// Manager, each a dropdown that just PICKS an existing value); RIGHT = the intro text + three inline-
// editable entity lists (Teams / Regions / Managers). Entity CRUD lives in those lists, Email-Recipients
// style: type to rename, clear to delete, a trailing blank field to add, auto-saved on blur — no popups
// and no per-cell "⋯". Team / Region / Manager are entities created once and assigned per rep
// (TRENDS-CAPABILITY-SPEC §3.2 / §7.1). Local state so edits apply without a full refetch. Fails quiet.

import * as api from '../lib/api.js';
import { customSelect, type CSOption, type CSInstance } from '../lib/customSelect.js';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

// The three org dimensions, each an entity kind. idField/nameField address the rep's assigned value on
// a TeamRepTrend; the fns route to the right API (teams has its own family; regions/managers are generic).
interface Dimension {
  label: string; // singular, capitalised (column header + list heading)
  plural: string;
  idField: 'teamId' | 'regionId' | 'managerId' | 'classId';
  nameField: 'teamName' | 'regionName' | 'managerName' | 'className';
  greyNames?: readonly string[]; // entries still matching these read as greyed "recommended" defaults
  list: () => Promise<api.OrgEntity[]>;
  create: (name: string) => Promise<{ ok: boolean; id?: string; error?: string }>;
  rename: (id: string, name: string) => Promise<{ ok: boolean; error?: string }>;
  remove: (id: string) => Promise<{ ok: boolean }>;
  assign: (repKey: string, id: string | null) => Promise<{ ok: boolean; error?: string }>;
}

// The two recommended classes seeded server-side; shown greyed until renamed. Keep in sync with
// DEFAULT_CLASSES in worker/src/db/orgEntities.ts.
const RECOMMENDED_CLASSES = ['Top Rep', 'Core Rep'] as const;

const asEntity = (t: api.Team): api.OrgEntity => ({ id: t.team_id, name: t.name, created_at: t.created_at, rep_count: t.rep_count });

const DIMENSIONS: Dimension[] = [
  {
    label: 'Team', plural: 'Teams', idField: 'teamId', nameField: 'teamName',
    list: () => api.listTeams().then((r) => r.teams.map(asEntity)),
    create: (name) => api.createTeam(name).then((r) => ({ ok: r.ok, id: r.team_id, error: r.error })),
    rename: (id, name) => api.renameTeam(id, name),
    remove: (id) => api.deleteTeam(id),
    assign: (repKey, id) => api.assignRepTeam(repKey, id),
  },
  {
    label: 'Region', plural: 'Regions', idField: 'regionId', nameField: 'regionName',
    list: () => api.listOrgEntities('regions'),
    create: (name) => api.createOrgEntity('regions', name),
    rename: (id, name) => api.renameOrgEntity('regions', id, name),
    remove: (id) => api.deleteOrgEntity('regions', id),
    assign: (repKey, id) => api.assignRepOrgEntity('regions', repKey, id),
  },
  {
    label: 'Manager', plural: 'Managers', idField: 'managerId', nameField: 'managerName',
    list: () => api.listOrgEntities('managers'),
    create: (name) => api.createOrgEntity('managers', name),
    rename: (id, name) => api.renameOrgEntity('managers', id, name),
    remove: (id) => api.deleteOrgEntity('managers', id),
    assign: (repKey, id) => api.assignRepOrgEntity('managers', repKey, id),
  },
  {
    label: 'Class', plural: 'Classes', idField: 'classId', nameField: 'className', greyNames: RECOMMENDED_CLASSES,
    list: () => api.listOrgEntities('classes'),
    create: (name) => api.createOrgEntity('classes', name),
    rename: (id, name) => api.renameOrgEntity('classes', id, name),
    remove: (id) => api.deleteOrgEntity('classes', id),
    assign: (repKey, id) => api.assignRepOrgEntity('classes', repKey, id),
  },
];

// Local page state.
let reps: api.TeamRepTrend[] = [];
const entities: Record<string, api.OrgEntity[]> = {}; // label → its values
const csHandles = new WeakMap<HTMLElement, CSInstance>(); // custom-select root → its handle (for refreshDropdowns)
let repSearch = '';

export function renderTeamsPage(): void {
  const root = document.getElementById('teams-page');
  if (!root) return;
  repSearch = '';
  root.textContent = '';
  root.appendChild(h('p', 'tm-loading', 'Loading…'));
  Promise.all([
    api.getTeamTrends(false).catch(() => ({ reps: [] as api.TeamRepTrend[] })),
    ...DIMENSIONS.map((d) => d.list().catch(() => [] as api.OrgEntity[])),
  ])
    .then(([trends, ...lists]) => {
      reps = (trends as { reps: api.TeamRepTrend[] }).reps;
      DIMENSIONS.forEach((d, i) => (entities[d.label] = lists[i] as api.OrgEntity[]));
      paint(root);
    })
    .catch(() => {
      root.textContent = '';
      root.appendChild(h('p', 'tt-empty', 'This page is unavailable right now. Please try again.'));
    });
}

function paint(root: HTMLElement): void {
  root.textContent = '';
  const layout = h('div', 'tms-layout');
  layout.appendChild(mainColumn()); // 70% — the rep grid
  layout.appendChild(sideColumn()); // 30% — intro + entity lists
  root.appendChild(layout);
  setTimeout(applyRepSearch, 0);
}

// ---- LEFT: rep grid --------------------------------------------------------------------------------
function mainColumn(): HTMLElement {
  const col = h('div', 'tms-main');

  if (!reps.length) {
    col.appendChild(h('p', 'tms-sub', 'No reps yet. As calls are scored, each rep appears here — then assign their Team, Region, Manager and Class from the lists on the right.'));
    return col;
  }

  const search = document.createElement('input');
  search.type = 'search';
  search.placeholder = 'Search reps…';
  search.className = 'tt-search tms-search';
  search.value = repSearch;
  search.setAttribute('aria-label', 'Search reps by name');
  search.addEventListener('input', () => { repSearch = search.value; applyRepSearch(); });
  col.appendChild(search);

  const scroll = h('div', 'tt-scroll');
  const table = h('table', 'tms-grid');
  const thead = h('thead');
  const htr = h('tr');
  htr.appendChild(h('th', undefined, 'Rep'));
  for (const d of DIMENSIONS) htr.appendChild(h('th', `tms-col-${d.label.toLowerCase()}`, d.label));
  thead.appendChild(htr);
  table.appendChild(thead);
  const tbody = h('tbody');
  for (const r of [...reps].sort((a, b) => a.name.localeCompare(b.name))) tbody.appendChild(repRow(r));
  table.appendChild(tbody);
  scroll.appendChild(table);
  col.appendChild(scroll);
  return col;
}

function applyRepSearch(): void {
  const q = repSearch.trim().toLowerCase();
  for (const tr of Array.from(document.querySelectorAll<HTMLTableRowElement>('#teams-page .tms-grid tbody tr'))) {
    const name = tr.querySelector<HTMLInputElement>('.tms-rep-input')?.value.toLowerCase() ?? '';
    tr.hidden = q !== '' && !name.includes(q);
  }
}

function repRow(r: api.TeamRepTrend): HTMLElement {
  const tr = h('tr');
  const who = h('td');
  const name = document.createElement('input');
  name.type = 'text';
  name.value = r.name;
  name.className = 'tm-name tms-rep-input';
  name.maxLength = 120;
  name.title = 'Click to rename this rep';
  const commit = (): void => {
    const v = name.value.trim();
    if (!v || v === r.name) { name.value = r.name; return; }
    name.disabled = true;
    api.renameRep(r.repKey, v).then(() => { r.name = v; name.disabled = false; }).catch(() => { name.disabled = false; name.value = r.name; });
  };
  name.addEventListener('blur', commit);
  name.addEventListener('keydown', (e) => { if (e.key === 'Enter') name.blur(); });
  who.appendChild(name);
  tr.appendChild(who);

  for (const d of DIMENSIONS) tr.appendChild(assignCell(r, d));
  return tr;
}

/** One dimension cell: a dropdown that PICKS an existing value (or none). Creating/renaming/deleting
 *  the values themselves happens in the right-hand lists — so no per-cell "⋯" and no popups here. */
function dimOptions(d: Dimension): CSOption[] {
  return [
    { id: '', name: `No ${d.label.toLowerCase()}` },
    ...(entities[d.label] ?? []).map((e) => ({ id: e.id, name: e.name })),
  ];
}

function assignCell(r: api.TeamRepTrend, d: Dimension): HTMLElement {
  const td = h('td', `tms-assign-cell tms-col-${d.label.toLowerCase()}`);
  const cs = customSelect({
    value: r[d.idField] ?? '',
    options: dimOptions(d),
    ariaLabel: `${d.label} for ${r.name}`,
    className: 'tt-team-select tms-select',
    onChange: (raw) => {
      const newId = raw || null;
      const prev = r[d.idField];
      cs.setDisabled(true);
      d.assign(r.repKey, newId)
        .then(() => setRepEntity(r, d, newId, newId ? (entities[d.label] ?? []).find((e) => e.id === newId)?.name ?? null : null))
        .catch(() => { r[d.idField] = prev; cs.setValue(prev ?? ''); })
        .finally(() => { cs.setDisabled(false); });
    },
  });
  cs.root.dataset.dim = d.label;
  cs.root.dataset.rep = r.repKey;
  csHandles.set(cs.root, cs);
  td.appendChild(cs.root);
  return td;
}

function setRepEntity(r: api.TeamRepTrend, d: Dimension, id: string | null, name: string | null): void {
  r[d.idField] = id;
  r[d.nameField] = name;
}

/** Rebuild every dropdown of a dimension in place (after its value list changed), preserving each
 *  rep's current selection. Keeps focus elsewhere — only the option list is replaced. */
function refreshDropdowns(d: Dimension): void {
  for (const root of Array.from(document.querySelectorAll<HTMLElement>(`#teams-page .tms-select[data-dim="${d.label}"]`))) {
    const cs = csHandles.get(root);
    if (!cs) continue;
    const rep = reps.find((r) => r.repKey === root.dataset.rep);
    cs.setOptions(dimOptions(d), rep ? (rep[d.idField] ?? '') : '');
  }
}

// ---- RIGHT: intro + inline entity lists ------------------------------------------------------------
function sideColumn(): HTMLElement {
  const col = h('div', 'tms-side');
  col.appendChild(
    h('p', 'tms-lead', 'Organise your reps so you can filter and compare them. Create, rename, or delete a Team, Region, Manager and Class for each rep. Class comes with two recommended values (shown greyed) — keep, rename, or delete them.'),
  );
  for (const d of DIMENSIONS) col.appendChild(entityListPanel(d));
  return col;
}

function entityListPanel(d: Dimension): HTMLElement {
  const panel = h('details', 'tms-entpanel');
  panel.open = false; // collapsed by default; click the summary to expand
  const summary = h('summary', 'tms-entpanel-h');
  summary.appendChild(h('span', 'tms-entpanel-title', d.plural));
  const assigned = (entities[d.label] ?? []).length;
  summary.appendChild(h('span', 'tms-entpanel-count', assigned ? String(assigned) : ''));
  panel.appendChild(summary);
  const list = h('div', 'tms-entlist');
  list.dataset.dim = d.label;
  for (const e of entities[d.label] ?? []) list.appendChild(entityField(d, e));
  list.appendChild(entityField(d, null)); // trailing blank = "add"
  panel.appendChild(list);
  const msg = h('span', 'tms-entmsg', '');
  msg.dataset.dim = d.label;
  msg.setAttribute('role', 'status');
  panel.appendChild(msg);
  return panel;
}

/** One editable field. `entity` present → rename (blank+blur deletes); null → the trailing "add" field. */
function entityField(d: Dimension, entity: api.OrgEntity | null): HTMLInputElement {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'tms-entinput';
  input.maxLength = 60;
  input.dataset.dim = d.label;
  if (entity) {
    input.value = entity.name;
    input.dataset.id = entity.id;
    if (d.greyNames?.includes(entity.name)) input.classList.add('tms-entinput-rec'); // recommended, untouched
  } else {
    input.placeholder = `Add a ${d.label.toLowerCase()}…`;
  }
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') input.blur(); });
  input.addEventListener('change', () => onEntityCommit(d, input));
  return input;
}

function flashEntMsg(d: Dimension, kind: 'ok' | 'err', text: string): void {
  const msg = document.querySelector<HTMLElement>(`#teams-page .tms-entmsg[data-dim="${d.label}"]`);
  if (!msg) return;
  msg.className = `tms-entmsg ${kind}`;
  msg.textContent = text;
  window.setTimeout(() => { if (msg.textContent === text) { msg.textContent = ''; msg.className = 'tms-entmsg'; } }, 2600);
}

function onEntityCommit(d: Dimension, input: HTMLInputElement): void {
  const v = input.value.trim();
  const id = input.dataset.id;

  // Existing field cleared → delete the entity (reps assigned to it become unassigned; data kept).
  if (id && v === '') {
    input.disabled = true;
    const ent = (entities[d.label] ?? []).find((e) => e.id === id);
    const affected = reps.filter((r) => r[d.idField] === id).length;
    d.remove(id)
      .then(() => {
        entities[d.label] = (entities[d.label] ?? []).filter((e) => e.id !== id);
        for (const r of reps) if (r[d.idField] === id) setRepEntity(r, d, null, null);
        input.remove();
        refreshDropdowns(d);
        flashEntMsg(d, 'ok', affected ? `Deleted “${ent?.name}” — ${affected} rep${affected === 1 ? '' : 's'} unassigned` : `Deleted “${ent?.name}”`);
      })
      .catch(() => { input.disabled = false; input.value = ent?.name ?? ''; flashEntMsg(d, 'err', 'Couldn’t delete.'); });
    return;
  }

  // Existing field renamed.
  if (id && v !== '') {
    const ent = (entities[d.label] ?? []).find((e) => e.id === id);
    if (!ent || ent.name === v) return;
    input.disabled = true;
    d.rename(id, v)
      .then((res) => {
        if (!res.ok) throw new Error(res.error);
        ent.name = v;
        for (const r of reps) if (r[d.idField] === id) r[d.nameField] = v;
        input.classList.toggle('tms-entinput-rec', !!d.greyNames?.includes(v)); // un-grey once it's no longer a default
        refreshDropdowns(d);
        flashEntMsg(d, 'ok', 'Saved');
      })
      .catch(() => { input.value = ent.name; flashEntMsg(d, 'err', 'Couldn’t rename.'); })
      .finally(() => { input.disabled = false; });
    return;
  }

  // Trailing blank field with a value → create; then this field becomes the new entity and a fresh
  // blank is appended after it, so the user can keep adding without any re-render or popup.
  if (!id && v !== '') {
    input.disabled = true;
    d.create(v)
      .then((res) => {
        if (!res.ok || !res.id) throw new Error(res.error);
        (entities[d.label] ??= []).push({ id: res.id, name: v, created_at: 0, rep_count: 0 });
        input.dataset.id = res.id; // this field now represents the created entity
        input.placeholder = '';
        const list = input.parentElement;
        if (list) list.appendChild(entityField(d, null)); // a fresh blank to add the next
        refreshDropdowns(d);
        flashEntMsg(d, 'ok', 'Added');
      })
      .catch(() => { flashEntMsg(d, 'err', 'Couldn’t add.'); })
      .finally(() => { input.disabled = false; });
  }
}
